import React from 'react';
import {useAppLocation, useAppNavigate} from '../app/navigation/index.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {
  OWNER_EQUIPMENT_REPORTS_RESOURCE,
  type OwnerEquipmentReport,
  type OwnerEquipmentReportsData,
} from '../app/data/ownerEquipmentReportsData.js';
import {useAuth} from '../contexts/AuthContext.js';
import {canReadOwnEquipmentReports} from '../lib/ownerReportAccess.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import Header from '../shared/Header.js';
import Button from '../shared/Button.js';
import InlineNotice from '../shared/InlineNotice.js';
import Skeleton from '../shared/Skeleton.js';
import {useToast} from '../shared/Toast.js';
import OwnerReportDrawer from './OwnerReportDrawer.js';

export default function OwnerEquipmentReports() {
  const {authState} = useAuth();
  const actor = authState ? {profileId: authState.user.id, role: authState.role} : null;
  return canReadOwnEquipmentReports(actor) ? (
    <ReportList key={actor.profileId + ':' + actor.role} actor={actor} />
  ) : (
    <InlineNotice notice={{kind: 'error', message: 'You do not have access to equipment reports.'}} />
  );
}
function ReportList({actor}: {actor: {profileId: string; role: string}}) {
  const resource = useRouteResource(OWNER_EQUIPMENT_REPORTS_RESOURCE, {profileId: actor.profileId});
  const location = useAppLocation();
  const navigate = useAppNavigate();
  const toast = useToast();
  const [hidden, setHidden] = React.useState<string[]>([]);
  const reportKey = new URLSearchParams(location.search).get('report');
  const reports = ((resource.data as OwnerEquipmentReportsData | null)?.reports || []).filter(
    (report) => !hidden.includes(report.kind + ':' + report.row.id),
  );
  const selected = reports.find((report) => report.kind + ':' + report.row.id === reportKey);
  const [activeReport, setActiveReport] = React.useState<OwnerEquipmentReport | null>(null);
  React.useEffect(() => {
    setActiveReport((current) => {
      if (!reportKey) return null;
      if (current && current.kind + ':' + current.row.id === reportKey) return current;
      // A failed list cannot open a new editor. An existing editor owns its
      // snapshot until explicit close/delete, including if a refresh omits it.
      return !resource.error && selected ? selected : null;
    });
  }, [reportKey, selected, resource.error]);
  const editing = activeReport && activeReport.kind + ':' + activeReport.row.id === reportKey ? activeReport : null;
  const close = () => navigate('/equipment?reports=mine');
  return (
    <div data-owner-equipment-reports="1">
      <Header />
      <main style={{maxWidth: 900, padding: '20px 16px', margin: '0 auto'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'}}>
          <h1 style={{fontSize: 26}}>My Equipment Reports</h1>
          <Button onClick={() => navigate('/equipment')}>Back To Equipment</Button>
        </div>
        <p style={{color: 'var(--text-2)'}}>Your fueling and fuel supply reports. Open a report to correct it.</p>
        {!!resource.error && (
          <InlineNotice
            notice={{kind: 'error', message: 'Could not load your reports. Retry before editing.'}}
            action={<Button onClick={() => void resource.refetch()}>Retry</Button>}
          />
        )}
        {resource.loading && !resource.data && <Skeleton lines={4} label="Loading your equipment reports" />}
        {!resource.loading && !resource.error && reports.length === 0 && (
          <p style={{color: 'var(--text-2)'}}>No equipment reports submitted by you yet.</p>
        )}
        {!resource.error && (
          <div style={{display: 'grid', gap: 8}}>
            {reports.map((report) => {
              const id = report.kind + ':' + report.row.id;
              return (
                <button
                  key={id}
                  type="button"
                  className="wcf-clickable"
                  data-owner-report={id}
                  onClick={() => navigate('/equipment?reports=mine&report=' + encodeURIComponent(id))}
                  style={{
                    padding: 16,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-control)',
                    textAlign: 'left',
                    font: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <strong>{report.kind === 'fueling' ? report.equipmentName : 'Fuel Supply'}</strong>
                  <div style={{color: 'var(--text-2)', fontSize: 13, marginTop: 4}}>
                    {fmt(report.row.date)} · {report.row.gallons ?? '—'} gal
                  </div>
                  <div style={{color: 'var(--text-2)', fontSize: 13, marginTop: 4}}>
                    {report.kind === 'supply'
                      ? report.row.destination
                      : `${report.unit === 'km' ? (report.row.km_reading ?? '—') : (report.row.hours_reading ?? '—')} ${report.unit === 'km' ? 'km' : 'hr'}`}{' '}
                    · {report.row.team_member || 'Unknown submitter'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {!resource.loading && !resource.error && reportKey && !selected && !editing && (
          <InlineNotice
            notice={{kind: 'error', message: 'This report is unavailable in your reports. It may have been deleted.'}}
            action={<Button onClick={close}>Back To Reports</Button>}
          />
        )}
        {reports.length >= 500 && (
          <p style={{color: 'var(--text-2)', fontSize: 13}}>Showing up to 500 recent reports of each kind.</p>
        )}
      </main>
      {editing && (
        <OwnerReportDrawer
          key={reportKey}
          client={sb}
          actor={actor}
          report={editing}
          onClose={() => {
            close();
            void resource.refetch();
          }}
          onDeleted={() => {
            setHidden((previous) => [...previous, editing.kind + ':' + editing.row.id]);
            toast.show({message: 'Report deleted. An administrator can restore it.'});
            close();
            void resource.refetch();
          }}
        />
      )}
    </div>
  );
}
